type JsonRecord = Record<string, any>;

export interface OnboardingAssetDesignContext {
  blockType?: string;
  blockName?: string;
  blockDescription?: string;
  sourceData?: JsonRecord;
  assetPromptPolicy?: Partial<OnboardingAssetPromptPolicy>;
  assetPromptPolicySource?: string;
}

export interface OnboardingAssetEnhancementSummary {
  styleLock: string;
  screenCount: number;
  slotCount: number;
  policySource: string;
}

export interface OnboardingAssetBlockFunction {
  primary: string;
  cognitive: string;
  emotional: string;
  recall: string;
}

export interface OnboardingAssetPromptPolicy {
  version: string;
  negativePrompt: string | string[];
  blockFunctions: Record<string, OnboardingAssetBlockFunction>;
  styleLockRules: string[];
  textPolicy: string;
  designObjectives: string[];
}

interface AssetSource {
  slotKey: string;
  asset: JsonRecord;
  order: number;
  slotRole: string;
  storyboardCardIndex?: number;
}

const DESIGN_PROMPT_MARKER = 'CONSISTENCY CONTEXT (shared across this onboarding block)';

export const DEFAULT_ONBOARDING_ASSET_PROMPT_POLICY: OnboardingAssetPromptPolicy = {
  version: '1',
  negativePrompt: [
    'No readable UI text, app labels, watermarks, brand logos, URLs, distorted anatomy, body shaming, medical claims, or impossible before/after promises.',
    'Do not render prompt section labels or internal slot keys.',
  ].join(' '),
  blockFunctions: {
    welcome: {
      primary: 'first-impression trust and relevance',
      cognitive: 'show in one glance that the user is in the right place',
      emotional: 'make the product feel safe, premium, and human',
      recall: 'attach the product promise to a simple visual memory',
    },
    user_goals: {
      primary: 'goal clarity and segmentation',
      cognitive: 'make choices easy to compare without extra reading',
      emotional: 'make each goal feel personally recognized',
      recall: 'turn each goal into a distinct visual label',
    },
    user_data: {
      primary: 'value exchange before data collection',
      cognitive: 'clarify why this data is needed and how it will be used',
      emotional: 'reduce anxiety around sharing personal details',
      recall: 'connect the question to the personalized output it unlocks',
    },
    history: {
      primary: 'empathetic acknowledgement',
      cognitive: 'make past friction legible without blaming the user',
      emotional: 'make the user feel understood and not judged',
      recall: 'give the past problem a simple visual representation',
    },
    habits: {
      primary: 'behavior context and objection handling',
      cognitive: 'show how the product adapts to real routines',
      emotional: 'make change feel achievable inside the user lifestyle',
      recall: 'represent the routine cue the product will work with',
    },
    environment: {
      primary: 'real-world fit',
      cognitive: 'show the relevant context where the product creates value',
      emotional: 'make the product feel practical, not abstract',
      recall: 'tie the next step to a recognizable environment',
    },
    motivation: {
      primary: 'motivation and perceived probability of success',
      cognitive: 'make the next benefit easy to understand quickly',
      emotional: 'show an aspirational but believable future state',
      recall: 'make the desired transformation visually memorable',
    },
    strategy: {
      primary: 'co-created plan confidence',
      cognitive: 'show structure, sequence, and progress with low complexity',
      emotional: 'make the plan feel personalized and under control',
      recall: 'give the strategy a recognizable visual framework',
    },
    plan_paywall: {
      primary: 'value demonstration and conversion readiness',
      cognitive: 'make the personalized plan and offer easy to evaluate',
      emotional: 'increase confidence that continuing is worth the effort',
      recall: 'make the aha moment and next step visually concrete',
    },
    custom: {
      primary: 'support the block hypothesis',
      cognitive: 'clarify the current screen message with minimal effort',
      emotional: 'increase motivation to continue',
      recall: 'make the core idea visually memorable',
    },
  },
  styleLockRules: [
    'Use consistent palette, lighting or illustration treatment, density, margins, camera distance, and one repeated motif across every asset in this block.',
    'Visuals must reduce cognitive load, make the text faster to understand, add a human/emotional cue, and create a memorable label for the screen idea.',
    'Aspirational scenes are allowed when they support motivation, but avoid false guarantees, shame, medical claims, or manipulative transformations.',
  ],
  textPolicy: 'Generate the visual foundation only. Avoid readable words unless the screen explicitly needs a simple symbolic label; never add UI labels, watermarks, URLs, or prompt metadata.',
  designObjectives: ['clarity', 'emotion', 'motivation', 'memory'],
};

function compact(value: unknown, maxChars = 220): string {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxChars);
}

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    const text = compact(value);
    if (text) return text;
  }
  return '';
}

function resolvePolicy(policy?: Partial<OnboardingAssetPromptPolicy>): OnboardingAssetPromptPolicy {
  return {
    ...DEFAULT_ONBOARDING_ASSET_PROMPT_POLICY,
    ...policy,
    negativePrompt: policy?.negativePrompt ?? DEFAULT_ONBOARDING_ASSET_PROMPT_POLICY.negativePrompt,
    blockFunctions: {
      ...DEFAULT_ONBOARDING_ASSET_PROMPT_POLICY.blockFunctions,
      ...(policy?.blockFunctions ?? {}),
    },
    styleLockRules: Array.isArray(policy?.styleLockRules) && policy.styleLockRules.length > 0
      ? policy.styleLockRules
      : DEFAULT_ONBOARDING_ASSET_PROMPT_POLICY.styleLockRules,
    designObjectives: Array.isArray(policy?.designObjectives) && policy.designObjectives.length > 0
      ? policy.designObjectives
      : DEFAULT_ONBOARDING_ASSET_PROMPT_POLICY.designObjectives,
    textPolicy: compact(policy?.textPolicy, 2_000) || DEFAULT_ONBOARDING_ASSET_PROMPT_POLICY.textPolicy,
  };
}

function negativePromptFromPolicy(policy: OnboardingAssetPromptPolicy): string {
  return Array.isArray(policy.negativePrompt)
    ? policy.negativePrompt.join(' ')
    : policy.negativePrompt;
}

function getBlockFunction(blockType?: string, policy = DEFAULT_ONBOARDING_ASSET_PROMPT_POLICY): OnboardingAssetBlockFunction {
  const key = String(blockType ?? '').toLowerCase();
  return policy.blockFunctions[key] ?? policy.blockFunctions.custom ?? DEFAULT_ONBOARDING_ASSET_PROMPT_POLICY.blockFunctions.custom;
}

function safeArray(value: unknown): JsonRecord[] {
  return Array.isArray(value) ? value.filter((item): item is JsonRecord => Boolean(item) && typeof item === 'object') : [];
}

function deriveSourceLabel(sourceData?: JsonRecord): string {
  const firstTask = safeArray(sourceData?.tasks)[0];
  const firstAudience = safeArray(sourceData?.audiences)[0];
  const firstActor = safeArray(firstAudience?.actors)[0];
  return firstNonEmpty(
    firstTask?.name,
    firstTask?.description,
    firstAudience?.name,
    firstAudience?.description,
    firstActor?.name,
    firstActor?.description,
  );
}

function buildStyleLock(
  block: JsonRecord,
  context: OnboardingAssetDesignContext,
  policy: OnboardingAssetPromptPolicy,
): string {
  const blockType = context.blockType ?? block.blockType;
  const fn = getBlockFunction(blockType, policy);
  const sourceLabel = deriveSourceLabel(context.sourceData);
  const subject = firstNonEmpty(
    context.blockName,
    block.name,
    context.blockDescription,
    block.description,
    sourceLabel,
    'the product promise',
  );

  return [
    `Keep one coherent onboarding visual system for "${subject}".`,
    `Primary visual function: ${fn.primary}.`,
    ...policy.styleLockRules,
  ].join(' ');
}

function slotRole(slotKey: string): string {
  if (slotKey === 'hero') return 'hero hook';
  if (slotKey === 'featured') return 'featured proof or context';
  if (slotKey.startsWith('option_')) return 'choice memory label';
  if (slotKey.startsWith('carousel_')) return 'story beat';
  if (slotKey.startsWith('stepper_')) return 'progress step cue';
  if (slotKey.startsWith('paywall_feature_')) return 'offer value proof';
  return 'supporting visual';
}

function screenTitle(screen: JsonRecord): string {
  const content = screen.content as JsonRecord | undefined;
  return firstNonEmpty(
    screen.name,
    content?.copy?.title,
    content?.carousel?.items?.[0]?.title,
    screen.description,
    screen.screenType,
  );
}

function buildPrompt(args: {
  originalPrompt: string;
  styleLock: string;
  blockType?: string;
  screen: JsonRecord;
  slotKey: string;
  slotRole: string;
  storyboardCardIndex?: number;
  policy: OnboardingAssetPromptPolicy;
}): string {
  if (args.originalPrompt.includes(DESIGN_PROMPT_MARKER)) {
    return args.originalPrompt;
  }
  const fn = getBlockFunction(args.blockType, args.policy);
  const indexLine = args.storyboardCardIndex !== undefined
    ? `- Story/card index: ${args.storyboardCardIndex}`
    : undefined;
  return [
    `${DESIGN_PROMPT_MARKER}:`,
    `- ${args.styleLock}`,
    '',
    'ONBOARDING DESIGN JOB:',
    `- Visual function: ${fn.primary}`,
    `- Cognitive job: ${fn.cognitive}`,
    `- Emotional job: ${fn.emotional}`,
    `- Recall job: ${fn.recall}`,
    '',
    'SCREEN ASSIGNMENT:',
    `- Screen: ${screenTitle(args.screen) || 'current onboarding screen'}`,
    `- Screen type: ${args.screen.screenType ?? 'unknown'}`,
    `- Slot: ${args.slotKey}`,
    `- Slot role: ${args.slotRole}`,
    indexLine,
    '- The image must clarify or emotionally support this exact screen, not become generic marketing art.',
    '- For indirect motivation, imply the desired future state through environment, posture, social context, or product outcome without making explicit false promises.',
    '',
    'IMAGE TASK:',
    args.originalPrompt,
    '',
    'TEXT POLICY:',
    args.policy.textPolicy,
  ].filter(Boolean).join('\n');
}

function shouldUseAsset(asset: unknown): asset is JsonRecord {
  return Boolean(
    asset
    && typeof asset === 'object'
    && (
      compact((asset as JsonRecord).promptTemplate).length > 0
      || compact((asset as JsonRecord).generatedUrl).length > 0
      || Boolean((asset as JsonRecord).experiments)
    ),
  );
}

function collectInlineAssetSources(content?: JsonRecord): AssetSource[] {
  const sources: AssetSource[] = [];
  const add = (slotKey: string, asset: unknown, order: number, storyboardCardIndex?: number) => {
    if (!shouldUseAsset(asset)) return;
    sources.push({
      slotKey,
      asset,
      order,
      storyboardCardIndex,
      slotRole: slotRole(slotKey),
    });
  };

  add('hero', content?.heroAsset, 0);
  add('featured', content?.featuredMediaAsset, 1);

  safeArray(content?.carousel?.items).forEach((item, index) => {
    add(`carousel_${index}`, item.asset ?? item.imageAsset, 20 + index, index);
  });

  safeArray(content?.options?.items).forEach((item, index) => {
    add(`option_${index}`, item.imageAsset, 100 + index);
  });

  safeArray(content?.stepper?.items).forEach((item, index) => {
    add(`stepper_${index}`, item.imageAsset, 200 + index, index);
  });

  safeArray(content?.paywall?.features).forEach((item, index) => {
    add(`paywall_feature_${index}`, item.imageAsset, 300 + index);
  });

  return sources;
}

function collectExplicitAssetSources(screen: JsonRecord, startingOrder: number): AssetSource[] {
  const explicit = screen.aiAssets;
  if (!explicit || typeof explicit !== 'object' || Array.isArray(explicit)) return [];

  return Object.entries(explicit as JsonRecord)
    .filter(([, asset]) => shouldUseAsset(asset))
    .map(([slotKey, asset], index) => ({
      slotKey,
      asset: asset as JsonRecord,
      order: typeof (asset as JsonRecord).order === 'number' ? (asset as JsonRecord).order : startingOrder + index,
      slotRole: compact((asset as JsonRecord).slotRole) || slotRole(slotKey),
      storyboardCardIndex: typeof (asset as JsonRecord).storyboardCardIndex === 'number'
        ? (asset as JsonRecord).storyboardCardIndex
        : slotKey.startsWith('carousel_') || slotKey.startsWith('stepper_')
          ? Number(slotKey.split('_').slice(-1)[0])
          : undefined,
    }));
}

function collectAssetSources(screen: JsonRecord, content?: JsonRecord): AssetSource[] {
  const byKey = new Map<string, AssetSource>();
  for (const source of collectInlineAssetSources(content)) byKey.set(source.slotKey, source);
  for (const source of collectExplicitAssetSources(screen, byKey.size + 400)) {
    const existing = byKey.get(source.slotKey);
    if (existing && typeof source.asset.order !== 'number') {
      byKey.set(source.slotKey, {
        ...source,
        order: existing.order,
        storyboardCardIndex: source.storyboardCardIndex ?? existing.storyboardCardIndex,
      });
      continue;
    }
    byKey.set(source.slotKey, source);
  }
  return Array.from(byKey.values()).sort((a, b) => a.order - b.order || a.slotKey.localeCompare(b.slotKey));
}

function setInlineAsset(content: JsonRecord | undefined, source: AssetSource, patch: JsonRecord): void {
  if (!content) return;
  const asset = source.asset;
  Object.assign(asset, patch);
}

function isSlotShape(value: JsonRecord): boolean {
  return Boolean(value.experiments && typeof value.experiments === 'object' && value.selectedExperimentId);
}

function toExperiment(asset: JsonRecord, source: AssetSource, generationModel?: string): JsonRecord {
  return {
    promptTemplate: asset.promptTemplate || '',
    negativePrompt: asset.negativePrompt,
    aspectRatio: asset.aspectRatio,
    imageSize: asset.imageSize,
    style: asset.style,
    model: asset.model,
    provider: asset.provider,
    quality: asset.quality,
    openaiSize: asset.openaiSize,
    referenceImages: asset.referenceImages,
    generatedUrl: asset.generatedUrl,
    generationModel: asset.generationModel || generationModel,
    generatedAt: asset.generatedAt,
    status: asset.status || (asset.generatedUrl ? 'completed' : 'draft'),
    error: asset.error,
    ...(source.storyboardCardIndex !== undefined
      ? { storyboardCardIndex: source.storyboardCardIndex }
      : {}),
    slotRole: asset.slotRole || source.slotRole,
    promptPackage: asset.promptPackage,
    order: source.order,
  };
}

export function buildScreenAiAssetSlots(
  screen: JsonRecord,
  simplifiedContent?: JsonRecord,
  options: { generationModel?: string; createExperimentId?: () => string } = {},
): Record<string, JsonRecord> {
  const sources = collectAssetSources(screen, simplifiedContent);
  const slots: Record<string, JsonRecord> = {};
  for (const source of sources) {
    const asset = source.asset;
    if (isSlotShape(asset)) {
      slots[source.slotKey] = {
        ...asset,
        order: typeof asset.order === 'number' ? asset.order : source.order,
      };
      continue;
    }

    const expId = options.createExperimentId?.()
      ?? `exp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    slots[source.slotKey] = {
      order: source.order,
      experiments: {
        [expId]: toExperiment(asset, source, options.generationModel),
      },
      selectedExperimentId: expId,
    };
  }
  return slots;
}

export function enhanceOnboardingBlockAssetPrompts(
  block: JsonRecord,
  context: OnboardingAssetDesignContext = {},
): OnboardingAssetEnhancementSummary {
  const screens = safeArray(block.screens);
  const policy = resolvePolicy(context.assetPromptPolicy);
  const policySource = context.assetPromptPolicySource ?? 'bundled-fallback';
  const styleLock = buildStyleLock(block, context, policy);
  let slotCount = 0;

  for (const screen of screens) {
    const content = screen.content as JsonRecord | undefined;
    const sources = collectAssetSources(screen, content);
    if (sources.length === 0) continue;

    const slotBriefs: Record<string, JsonRecord> = {};
    const explicitAiAssets: Record<string, JsonRecord> = {};

    for (const source of sources) {
      const promptTemplate = compact(source.asset.promptTemplate, 20_000);
      if (!promptTemplate && !source.asset.generatedUrl) continue;
      const enrichedPrompt = promptTemplate
        ? buildPrompt({
            originalPrompt: promptTemplate,
            styleLock,
            blockType: context.blockType ?? block.blockType,
            screen,
            slotKey: source.slotKey,
            slotRole: source.slotRole,
            storyboardCardIndex: source.storyboardCardIndex,
            policy,
          })
        : promptTemplate;
      const patch = {
        ...source.asset,
        promptTemplate: enrichedPrompt,
        negativePrompt: source.asset.negativePrompt || negativePromptFromPolicy(policy),
        slotRole: source.asset.slotRole || source.slotRole,
        ...((source.asset.storyboardCardIndex ?? source.storyboardCardIndex) !== undefined
          ? { storyboardCardIndex: source.asset.storyboardCardIndex ?? source.storyboardCardIndex }
          : {}),
      };
      setInlineAsset(content, source, patch);
      explicitAiAssets[source.slotKey] = patch;
      slotBriefs[source.slotKey] = {
        slotRole: patch.slotRole,
        ...(patch.storyboardCardIndex !== undefined
          ? { storyboardCardIndex: patch.storyboardCardIndex }
          : {}),
        visualFunction: getBlockFunction(context.blockType ?? block.blockType, policy).primary,
      };
      slotCount += 1;
    }

    screen.aiAssets = {
      ...((screen.aiAssets && typeof screen.aiAssets === 'object') ? screen.aiAssets : {}),
      ...explicitAiAssets,
    };
    screen.aiMeta = {
      ...(screen.aiMeta ?? {}),
      assetDesignBrief: {
        version: 1,
        source: policySource,
        policyVersion: policy.version,
        styleLock,
        screenRole: screen.aiMeta?.blockRole || screen.description || screen.name || screen.screenType,
        designObjectives: policy.designObjectives,
        slots: slotBriefs,
      },
    };
  }

  return {
    styleLock,
    screenCount: screens.length,
    slotCount,
    policySource,
  };
}
